import { useLocation } from "react-router-dom";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ViewSidebarOutlined from "@mui/icons-material/ViewSidebarOutlined";
import ViewSidebar from "@mui/icons-material/ViewSidebar";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import Grid from "@mui/material/Grid";
import VuiBox from "components/ui/VuiBox";
import CompetitorSelector from "components/EntitySelector/CompetitorSelector";
import DateRangeSelector from "components/DateRangeSelector/DateRangeSelector";
import EntitySelector from "components/EntitySelector";
import {
  navbar,
  navbarContainer,
  navbarMobileMenu,
} from "components/navigation/DashboardNavbar/styles";
import {
  useUIController,
  setMiniSidenav,
} from "context";
import { useLayout } from "context/LayoutContext";
import PressSelector from "components/EntitySelector/PressesSelector";
import AIActionButton from "components/AIActionButton";
import { useQuickCatchup } from "context/QuickCatchupContext";
import { ROUTES, PAGE_SELECTOR_CONFIG, DEFAULT_PRODUCTS } from "utils/routeConstants";
import { useAnalytics } from "context/AnalyticsContext";
import { CONDITION_CATEGORIES, CONDITION_CATEGORY_LABELS } from "pages/rd-landscape/data/useRDLandscapeData";

const CONDITION_OPTIONS = Object.keys(CONDITION_CATEGORIES).map((key) => ({
  id: key,
  name: CONDITION_CATEGORY_LABELS[key],
}));

function DashboardNavbar({ absolute, light, isMini, onDownloadReport, noSelectors }) {
  const theme = useTheme();
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));
  const [controller, dispatch] = useUIController();
  const { miniSidenav } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const currentPage = route[0] === "v2" ? `v2/${route[1] || ""}` : route[0];
  const { rdConditionFilter, setRdConditionFilter, rdCompetitorId, setRdCompetitorId,
          showdownAnchor, setShowdownAnchor, showdownCompetitor, setShowdownCompetitor,
          showdownAnchors, showdownCompetitors } = useAnalytics();

  const pageConfig = PAGE_SELECTOR_CONFIG[currentPage] || {};
  const { action: quickCatchupAction } = useQuickCatchup();
  const layout = useLayout();

  const [selectedOurProduct, setSelectedOurProduct] = useState(DEFAULT_PRODUCTS.OUR_PRODUCTS[0]?.id || null);
  const [selectedCompetingProduct, setSelectedCompetingProduct] = useState(DEFAULT_PRODUCTS.COMPETING_PRODUCTS[0]?.id || null);

  const handleMiniSidenav = () => {
    if (window.toggleSidenav) {
      window.toggleSidenav();
    } else {
      setMiniSidenav(dispatch, !miniSidenav);
    }
  };

  const renderLeftSelectors = () => {
    const selectors = [];

    if (pageConfig.showAnchorSelector) {
      selectors.push(
        <Grid item key="anchor" xs={12} sm="auto">
          <EntitySelector
            entities={showdownAnchors}
            selectedEntityId={showdownAnchor || ""}
            onEntityChange={setShowdownAnchor}
            label="Terumo Products"
            icon="inventory_2"
            width="185px"
          />
        </Grid>
      );
    }

    if (pageConfig.showCompetitorSelector) {
      selectors.push(
        <Grid item key="competitor" xs={12} sm="auto">
          <CompetitorSelector
            customOptions={pageConfig.customCompetitorOptions || []}
            {...(pageConfig.useRdCompetitorContext && {
              externalValue: rdCompetitorId,
              onExternalChange: setRdCompetitorId,
            })}
            {...(pageConfig.useShowdownCompetitorContext && {
              customOptions: showdownCompetitors,
              exclusiveOptions: true,
              label: "Competing Products",
              externalValue: showdownCompetitor || "",
              onExternalChange: setShowdownCompetitor,
            })}
          />
        </Grid>
      );
    }

    if (pageConfig.showPressSelector) {
      selectors.push(
        <Grid item key="press" xs={12} sm="auto">
          <PressSelector />
        </Grid>
      );
    }

    if (pageConfig.showConditionSelector) {
      selectors.push(
        <Grid item key="condition" xs={12} sm="auto">
          <EntitySelector
            entities={CONDITION_OPTIONS}
            selectedEntityId={rdConditionFilter}
            onEntityChange={setRdConditionFilter}
            label="Condition"
            icon="biotech"
            width="220px"
          />
        </Grid>
      );
    }

    if (pageConfig.showDateRangeSelector) {
      selectors.push(
        <Grid item key="dateRange" xs={12} sm="auto">
          <DateRangeSelector />
        </Grid>
      );
    }

    if (pageConfig.showProductSelectors) {
      selectors.push(
        <React.Fragment key="productSelectors">
          <Grid item xs={12} sm={6} md="auto">
            <EntitySelector
              entities={DEFAULT_PRODUCTS.OUR_PRODUCTS}
              selectedEntityId={selectedOurProduct}
              onEntityChange={setSelectedOurProduct}
              placeholder="Select Our Product"
              label="Our Product"
              icon="inventory_2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md="auto">
            <EntitySelector
              entities={DEFAULT_PRODUCTS.COMPETING_PRODUCTS}
              selectedEntityId={selectedCompetingProduct}
              onEntityChange={setSelectedCompetingProduct}
              placeholder="Select Competing Product"
              label="Competing Product"
              icon="shield"
            />
          </Grid>
        </React.Fragment>
      );
    }

    if (selectors.length === 0) return null;

    return (
      <VuiBox
        mx={1}
        display="flex"
        alignItems="center"
        sx={{
          flexWrap: "wrap",
          gap: 1,
          minWidth: 0,
          maxWidth: "100%",
          flex: { xs: 1, md: "none" },
          justifyContent: { xs: "flex-start", md: "flex-start" },
          pb: { xs: 1, md: 0 },
          pt: { xs: 1, md: 0 },
          px: { xs: 1, md: 0 },
        }}
      >
        <Grid container spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap" }}>
          {selectors}
        </Grid>
      </VuiBox>
    );
  };

  return (
    <AppBar
      position="absolute"
      color="inherit"
      sx={(t) => ({
        ...navbar(t, { transparentNavbar: absolute, absolute, light }),
        ...(isBelowLg
          ? {
              marginLeft: 0,
              width: "100%",
            }
          : {
              marginLeft: `-${t.spacing(3)}`,
              width: `calc(100% + ${t.spacing(3)} * 2)`,
            }),
      })}
    >
      <Toolbar sx={(th) => ({
        ...navbarContainer(th),
        ...(layout && {
          paddingLeft: `${th.spacing(layout.p - 3)} !important`,
        }),
        flexWrap: "wrap",
        rowGap: th.spacing(1),
        columnGap: th.spacing(1),
      })}>
        {isBelowLg ? (
          <VuiBox
            display="flex"
            alignItems="center"
            width="100%"
            sx={{
              minWidth: 0,
              flexWrap: "wrap",
              rowGap: 1,
              columnGap: 0.5,
            }}
          >
            <Tooltip title={miniSidenav ? "Expand sidebar" : "Collapse sidebar"} enterDelay={400}>
              <IconButton
                size="small"
                color="inherit"
                aria-label={miniSidenav ? "Expand navigation sidebar" : "Collapse navigation sidebar"}
                sx={{
                  ...navbarMobileMenu,
                  alignSelf: "center",
                  flexShrink: 0,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                }}
                onClick={handleMiniSidenav}
              >
                {miniSidenav ? (
                  <ViewSidebarOutlined sx={{ fontSize: 22, color: "common.white" }} />
                ) : (
                  <ViewSidebar sx={{ fontSize: 22, color: "common.white" }} />
                )}
              </IconButton>
            </Tooltip>
            <VuiBox mr={{ xs: 0, md: 6 }} sx={{ flexShrink: 0 }} />
            <VuiBox sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
              {!noSelectors && renderLeftSelectors()}
            </VuiBox>
            {(quickCatchupAction || onDownloadReport) && !isMini && (
              <VuiBox display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0, ml: "auto" }}>
                {quickCatchupAction && (
                  <AIActionButton iconOnly text="Quick Catchup" onClick={quickCatchupAction} />
                )}
                {onDownloadReport && (
                  <Tooltip title="Download PDF" enterDelay={400}>
                    <IconButton
                      size="small"
                      onClick={onDownloadReport}
                      aria-label="Download PDF"
                      sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        color: "common.white",
                        bgcolor: "rgba(255,255,255,0.1)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                      }}
                    >
                      <PictureAsPdf sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </VuiBox>
            )}
          </VuiBox>
        ) : (
          <>
            <VuiBox
              display="flex"
              alignItems="center"
              sx={{ minWidth: 0, flex: { xs: 1, md: "1 1 auto" }, flexWrap: "wrap", rowGap: 1, columnGap: 0.5 }}
            >
              <VuiBox mr={{ xs: 0, md: 6 }} sx={{ flexShrink: 0 }} />
              {!noSelectors && renderLeftSelectors()}
            </VuiBox>

            <VuiBox sx={{ flexGrow: 1 }} />

            <VuiBox display="flex" alignItems="flex-end">
              {quickCatchupAction && !isMini && (
                <VuiBox mr={2} sx={{ marginTop: "20px" }}>
                  <AIActionButton text="Quick Catchup" onClick={quickCatchupAction} />
                </VuiBox>
              )}

              {onDownloadReport && !isMini && (
                <VuiBox mr={2} sx={{ marginTop: "20px" }}>
                  <AIActionButton text="Download PDF" onClick={onDownloadReport} />
                </VuiBox>
              )}
            </VuiBox>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  onDownloadReport: null,
  noSelectors: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  onDownloadReport: PropTypes.func,
  noSelectors: PropTypes.bool,
};

export default DashboardNavbar;
